import {useLayoutEffect} from 'react'
import {toast} from 'react-toastify'
import {useMobileViewport} from './useMobileViewport'

const protectedRegions =
  '.mobile-common-footer, [data-cy="setting-confirm-footer"], .dopamine-composer-container'
const inactiveOwner =
  '[hidden], [inert], [aria-hidden="true"], [data-mobile-pane-active="false"]'
const offsetProperty = '--mobile-toast-bottom'

/** Move existing bottom toasts above visible action bars without resetting them. */
export function useMobileToastPlacement(): void {
  const isMobile = useMobileViewport()

  useLayoutEffect(() => {
    if (!isMobile) return undefined
    const root = document.documentElement
    let disposed = false
    let stopPlacement: (() => void) | undefined
    const hasToast = () =>
      Boolean(
        document.querySelector('.Toastify__toast-container .Toastify__toast'),
      )

    function syncPresence() {
      if (disposed) return
      if (hasToast()) {
        stopPlacement ??= observePlacement()
      } else if (stopPlacement) {
        stopPlacement()
        stopPlacement = undefined
      }
    }

    function observePlacement() {
      let frame = 0
      let disposed = false
      const observed = new Set<HTMLElement>()
      const viewport = window.visualViewport

      const schedule = () => {
        if (!frame && !disposed) frame = requestAnimationFrame(measure)
      }
      const resize = new ResizeObserver(schedule)
      const isVisible = (element: HTMLElement) => {
        if (element.closest(inactiveOwner) || !element.getClientRects().length)
          return false
        for (
          let node: HTMLElement | null = element;
          node;
          node = node.parentElement
        ) {
          const style = getComputedStyle(node)
          if (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.opacity === '0'
          )
            return false
        }
        return true
      }
      function measure() {
        frame = 0
        if (disposed) return
        if (!hasToast()) {
          syncPresence()
          return
        }
        const visual = viewport?.scale === 1 ? viewport : null
        const visibleTop = visual?.offsetTop ?? 0
        const visibleBottom =
          visibleTop + (visual?.height ?? window.innerHeight)
        const visibleLeft = visual?.offsetLeft ?? 0
        const visibleRight = visibleLeft + (visual?.width ?? window.innerWidth)
        let bottom = Math.max(12, window.innerHeight - visibleBottom + 12)
        const regions = new Set(
          Array.from(document.querySelectorAll<HTMLElement>(protectedRegions))
            .map(element =>
              element.matches('[data-cy="setting-confirm-footer"]')
                ? element.parentElement
                : element,
            )
            .filter((element): element is HTMLElement => Boolean(element)),
        )
        observed.forEach(element => {
          if (!regions.has(element)) {
            resize.unobserve(element)
            observed.delete(element)
          }
        })
        regions.forEach(element => {
          if (!observed.has(element)) {
            resize.observe(element)
            observed.add(element)
          }
          if (!isVisible(element)) return
          const rect = element.getBoundingClientRect()
          if (
            rect.height <= 0 ||
            rect.width <= 0 ||
            rect.top >= visibleBottom ||
            rect.bottom <= visibleTop ||
            rect.right <= visibleLeft ||
            rect.left >= visibleRight
          )
            return
          bottom = Math.max(
            bottom,
            window.innerHeight - Math.max(rect.top, visibleTop) + 8,
          )
        })
        const value = `${Math.ceil(bottom)}px`
        if (root.style.getPropertyValue(offsetProperty) !== value)
          root.style.setProperty(offsetProperty, value)
      }
      // Observe body, not html: writing our html-level custom property cannot feed
      // back into this observer. Size changes only observe the protected controls.
      const mutations = new MutationObserver(schedule)
      mutations.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'class',
          'style',
          'hidden',
          'inert',
          'aria-hidden',
          'data-mobile-pane-active',
          'data-expanded',
        ],
      })
      window.addEventListener('resize', schedule)
      window.addEventListener('scroll', schedule, true)
      document.body.addEventListener('transitionrun', schedule, true)
      document.body.addEventListener('transitionend', schedule, true)
      document.body.addEventListener('transitioncancel', schedule, true)
      viewport?.addEventListener('resize', schedule)
      viewport?.addEventListener('scroll', schedule)
      measure()
      return () => {
        disposed = true
        cancelAnimationFrame(frame)
        mutations.disconnect()
        resize.disconnect()
        window.removeEventListener('resize', schedule)
        window.removeEventListener('scroll', schedule, true)
        document.body.removeEventListener('transitionrun', schedule, true)
        document.body.removeEventListener('transitionend', schedule, true)
        document.body.removeEventListener('transitioncancel', schedule, true)
        viewport?.removeEventListener('resize', schedule)
        viewport?.removeEventListener('scroll', schedule)
        root.style.removeProperty(offsetProperty)
      }
    }

    // Toastify 8 emits its count after rendering. A zero count starts exit;
    // retain placement until the active DOM observer sees the final node leave.
    const unsubscribe = toast.onChange(syncPresence)
    syncPresence()
    return () => {
      disposed = true
      unsubscribe()
      stopPlacement?.()
      root.style.removeProperty(offsetProperty)
    }
  }, [isMobile])
}
